/**
 * Claves de API para integraciones externas.
 *
 * Detalle importante: el backend solo guarda el hash SHA-256 de cada clave, de
 * modo que la clave en claro unicamente existe en la respuesta de creacion. Se
 * muestra una vez, en un bloque destacado que no desaparece hasta que el usuario
 * lo cierra o cambia de pantalla.
 */
import { useCallback, useEffect, useState } from 'react';
import type { FormEvent, ReactElement } from 'react';
import type { ApiKeyView, CreateApiKeyResponse } from '@shared/api-types.ts';
import { api, errorMessage, isAbortError } from '../api.ts';
import { formatDateTime } from '../format.ts';
import type { NoticeKind } from '../hooks.ts';
import { Chip, EmptyState, ErrorBanner, Field, SkeletonRows, Spinner } from './Feedback.tsx';
import { Icon } from './Icon.tsx';

type State =
  | { status: 'loading' }
  | { status: 'ready'; keys: ApiKeyView[] }
  | { status: 'error'; message: string }
  /** Un agente raso no puede ni listar las claves: la API responde 403. */
  | { status: 'restricted' };

/**
 * Respaldo de copiado: `navigator.clipboard` solo existe en contexto seguro
 * (HTTPS o localhost), asi que en el resto de casos se usa la seleccion clasica.
 */
function fallbackCopy(text: string): boolean {
  try {
    const area = document.createElement('textarea');
    area.value = text;
    area.setAttribute('readonly', '');
    area.style.position = 'fixed';
    area.style.top = '0';
    area.style.opacity = '0';
    document.body.appendChild(area);
    area.select();
    const ok = document.execCommand('copy');
    document.body.removeChild(area);
    return ok;
  } catch {
    return false;
  }
}

export function ApiKeysScreen({
  onNotice,
  canWrite,
}: {
  onNotice: (kind: NoticeKind, text: string) => void;
  /** Solo propietarios y administradores pueden crear o revocar (la API responde 403). */
  canWrite: boolean;
}): ReactElement {
  const [state, setState] = useState<State>({ status: 'loading' });
  const [reloadToken, setReloadToken] = useState(0);

  const [name, setName] = useState('');
  const [creating, setCreating] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  /** Clave recien creada: es la unica vez que se puede mostrar en claro. */
  const [created, setCreated] = useState<CreateApiKeyResponse | null>(null);
  const [copied, setCopied] = useState(false);

  const [pendingRevoke, setPendingRevoke] = useState<string | null>(null);
  const [revoking, setRevoking] = useState<string | null>(null);

  useEffect(() => {
    // Sin rol de administrador la API responde 403 incluso al listar, asi que no
    // se lanza una peticion condenada a fallar.
    if (!canWrite) {
      setState({ status: 'restricted' });
      return;
    }

    const controller = new AbortController();
    setState({ status: 'loading' });
    api
      .listApiKeys(controller.signal)
      .then((keys) => setState({ status: 'ready', keys }))
      .catch((error: unknown) => {
        if (isAbortError(error)) return;
        setState({ status: 'error', message: errorMessage(error) });
      });
    return () => controller.abort();
  }, [canWrite, reloadToken]);

  const handleCreate = useCallback(
    (event: FormEvent<HTMLFormElement>): void => {
      event.preventDefault();
      const cleanName = name.trim();
      if (!cleanName) {
        setFormError('Ponle un nombre a la clave para reconocerla despues.');
        return;
      }

      setFormError(null);
      setCreating(true);
      api
        .createApiKey({ name: cleanName })
        .then((response) => {
          setState((previous) => ({
            status: 'ready',
            keys: previous.status === 'ready' ? [response.key, ...previous.keys] : [response.key],
          }));
          setCreated(response);
          setCopied(false);
          setName('');
          onNotice('success', 'Clave creada. Copiala ahora: no se volvera a mostrar.');
        })
        .catch((error: unknown) => setFormError(errorMessage(error)))
        .finally(() => setCreating(false));
    },
    [name, onNotice],
  );

  const handleCopy = useCallback((): void => {
    if (!created) return;
    const secret = created.apiKey;
    const done = (ok: boolean): void => {
      setCopied(ok);
      onNotice(
        ok ? 'success' : 'warning',
        ok
          ? 'Clave copiada al portapapeles.'
          : 'No se pudo copiar automaticamente: selecciona la clave y copiala a mano.',
      );
    };

    const clipboard: Clipboard | undefined = navigator.clipboard;
    if (clipboard && typeof clipboard.writeText === 'function') {
      void clipboard
        .writeText(secret)
        .then(() => done(true))
        .catch(() => done(fallbackCopy(secret)));
      return;
    }
    done(fallbackCopy(secret));
  }, [created, onNotice]);

  const handleRevoke = useCallback(
    (key: ApiKeyView): void => {
      setRevoking(key.id);
      api
        .revokeApiKey(key.id)
        .then(() => {
          setState((previous) =>
            previous.status === 'ready'
              ? { status: 'ready', keys: previous.keys.filter((item) => item.id !== key.id) }
              : previous,
          );
          setPendingRevoke(null);
          onNotice('success', `Clave "${key.name}" revocada.`);
        })
        .catch((error: unknown) => onNotice('error', errorMessage(error)))
        .finally(() => setRevoking(null));
    },
    [onNotice],
  );

  const pendingKey =
    state.status === 'ready' && pendingRevoke
      ? (state.keys.find((item) => item.id === pendingRevoke) ?? null)
      : null;

  return (
    <div className="page">
      <header className="page__header">
        <div>
          <h1 className="page__title">Claves de API</h1>
          <p className="page__subtitle">
            Credenciales para que un CRM, un ERP o un script usen la API de esta organizacion sin
            una cuenta de usuario.
          </p>
        </div>
        <button
          type="button"
          className="button button--ghost"
          onClick={() => setReloadToken((token) => token + 1)}
        >
          <Icon name="refresh" />
          Actualizar
        </button>
      </header>

      {created ? (
        <section className="card secret">
          <div className="card__row">
            <div>
              <h2 className="card__title">Clave creada: {created.key.name}</h2>
              <p className="card__hint">
                Guardala en tu gestor de secretos antes de cerrar este bloque.
              </p>
            </div>
            <button
              type="button"
              className="button button--ghost button--sm"
              onClick={() => {
                setCreated(null);
                setCopied(false);
              }}
            >
              <Icon name="close" />
              Ya la he guardado
            </button>
          </div>

          <div className="banner banner--warning" role="alert">
            <Icon name="warning" className="banner__icon" />
            <p className="banner__text">
              Esta es la unica vez que se muestra la clave completa: en el servidor solo se guarda
              su huella, asi que no se puede recuperar. Si la pierdes, revocala y crea otra.
            </p>
          </div>

          <div className="secret__row">
            <input
              className="input input--code"
              value={created.apiKey}
              readOnly
              aria-label="Clave de API recien creada"
              onFocus={(event) => event.currentTarget.select()}
            />
            <button type="button" className="button button--primary" onClick={handleCopy}>
              <Icon name={copied ? 'check' : 'copy'} />
              {copied ? 'Copiada' : 'Copiar'}
            </button>
            {copied ? <Chip tone="success">En el portapapeles</Chip> : null}
          </div>
        </section>
      ) : null}

      <section className="card">
        <h2 className="card__title">Nueva clave</h2>

        {!canWrite ? (
          <div className="banner banner--warning" role="status">
            <Icon name="warning" className="banner__icon" />
            <p className="banner__text">
              Solo el propietario y los administradores pueden gestionar las claves de API. Pide a
              un administrador de la organizacion que la cree.
            </p>
          </div>
        ) : null}

        <fieldset className="fieldset" disabled={!canWrite}>
          <form onSubmit={handleCreate}>
            <Field
              label="Nombre"
              htmlFor="api-key-name"
              required
              hint="Sirve para reconocer la integracion: por ejemplo «CRM de ventas»."
            >
              <input
                id="api-key-name"
                className="input"
                maxLength={120}
                value={name}
                placeholder="Integracion con el CRM"
                onChange={(event) => setName(event.target.value)}
                disabled={creating}
              />
            </Field>

            {formError ? (
              <div className="banner banner--error" role="alert">
                <Icon name="error" className="banner__icon" />
                <p className="banner__text">{formError}</p>
              </div>
            ) : null}

            <div className="page__actions page__actions--end">
              <button type="submit" className="button button--primary" disabled={creating}>
                {creating ? <Spinner label="Creando…" /> : (<><Icon name="plus" />Crear clave</>)}
              </button>
            </div>
          </form>
        </fieldset>
      </section>

      {pendingKey ? (
        <div className="banner banner--warning" role="alert">
          <Icon name="warning" className="banner__icon" />
          <p className="banner__text">
            Vas a revocar «{pendingKey.name}» (prefijo {pendingKey.keyPrefix}…). La integracion que
            use esta clave dejara de funcionar de inmediato.
          </p>
          <button
            type="button"
            className="button button--danger button--sm"
            onClick={() => handleRevoke(pendingKey)}
            disabled={revoking === pendingKey.id}
          >
            {revoking === pendingKey.id ? (
              <Spinner label="Revocando…" />
            ) : (
              'Confirmar revocacion'
            )}
          </button>
          <button
            type="button"
            className="button button--ghost button--sm"
            onClick={() => setPendingRevoke(null)}
            disabled={revoking === pendingKey.id}
          >
            Cancelar
          </button>
        </div>
      ) : null}

      {state.status === 'loading' ? <SkeletonRows rows={4} /> : null}

      {state.status === 'error' ? (
        <ErrorBanner message={state.message} onRetry={() => setReloadToken((token) => token + 1)} />
      ) : null}

      {state.status === 'ready' && state.keys.length === 0 ? (
        <EmptyState
          icon="key"
          title="No hay claves activas"
          description="Crea la primera clave para conectar la plataforma con tus sistemas."
        />
      ) : null}

      {state.status === 'ready' && state.keys.length > 0 ? (
        <div className="tablewrap">
          <table className="table">
            <thead>
              <tr>
                <th>Nombre</th>
                <th>Prefijo</th>
                <th>Creada</th>
                <th>Ultimo uso</th>
                <th>Acciones</th>
              </tr>
            </thead>
            <tbody>
              {state.keys.map((item) => (
                <tr key={item.id}>
                  <td className="table__strong">{item.name}</td>
                  <td className="table__mono">{item.keyPrefix}…</td>
                  <td>{formatDateTime(item.createdAt)}</td>
                  <td>{item.lastUsedAt ? formatDateTime(item.lastUsedAt) : 'Nunca usada'}</td>
                  <td>
                    {pendingRevoke === item.id ? (
                      <span className="table__mono">Confirmacion pendiente arriba</span>
                    ) : (
                      <button
                        type="button"
                        className="button button--ghost button--sm"
                        onClick={() => setPendingRevoke(item.id)}
                        disabled={!canWrite}
                        title={
                          canWrite
                            ? 'La integracion que la use dejara de funcionar'
                            : 'Solo los administradores pueden revocar claves'
                        }
                      >
                        <Icon name="trash" />
                        Revocar
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}
    </div>
  );
}
